import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIから通知数の取得が成功した場合に呼び出されます
   * @callback success
   * @param {number} unread - 通知の未読数
   * @param {number} total - 通知の全数
   * @param {number} status - httpステータス
   */
  success: (unread: number, total: number, status: number) => void;
}

export interface IGetNoticeCountResponceModel {
  /**
   * 通知の未読数
   */
  unread: number;
  /**
   * 通知の全数
   */
  total: number;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: { Authorization: "Bearer " + token },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const data: IGetNoticeCountResponceModel = res.data;
      params.success(data.unread, data.total, res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetNoticeCountParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
}

/**
 * 通知数を取得します
 * @function getNoticeCount
 * @param {IGetNoticeCountParams} params - 通知数取得パラメータ
 */
export function getNoticeCount(params: IGetNoticeCountParams): void {
  const { authService } = params;
  const url = WebAPI.GET_NOTICES_COUNTS;
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getNoticeCountRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * 通知数を取得します
 * @function getNoticeCountRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getNoticeCountRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}
